import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import posed from 'react-pose';
import css from '../style.css';
import AdNetworkSelection from './AdNetworkSelection';
import RequestIntegrationMessage from './RequestIntegrationMessage';
import RequestIntegrationForm from './RequestIntegrationForm';
import { Button } from '../../components/widgets';

const Z_INDEX = { SHOW: 3, HIDE: 0 };
const AnimationItem = posed.div({
    enter: {
        opacity: 1,
        zIndex: 5,
        x: 0,
        transition: {
            x: {
                type: 'tween',
                duration: 200,
                ease: 'anticipate',
            },
        },
    },
    exit: {
        opacity: 0,
        zIndex: 3,
        x: 150,
        transition: {
            x: {
                type: 'spring',
                duration: 0,
            },
        },
    },
});

const AddNewUan = ({
    adNetworkOptions = [],
    selectedAdNetworkOption = {},
    onAdNetworkSelectionChange,
    buttonText = '',
    placeholderText = '',
    onInputChange = () => {},
    icon = null,
    enableIntegrationRequest = false,
    onIntegrationRequested = () => {},
    translationPageKey,
}) => {
    const [showAdNetworkSelection, setShowAdNetworkSelection] = useState(false);
    const [requestMode, setRequestMode] = useState(false);
    const [requestName, setRequestName] = useState('');
    // Kept in sync with `requestMode` so the blur handler reads the current value without
    // depending on a stale closure.
    const requestModeRef = useRef(false);
    const containerRef = useRef(null);

    const setRequestModeSafe = useCallback(value => {
        requestModeRef.current = value;
        setRequestMode(value);
    }, []);

    const showAdNetworkSelectionHandler = useCallback(
        element => {
            if (element && element.currentTarget.id === 'uanNameText' && !showAdNetworkSelection) {
                return;
            }
            setShowAdNetworkSelection(true);
        },
        [showAdNetworkSelection]
    );

    const hideAdNetworkSelectionHandler = useCallback(() => {
        // Keep the dropdown open while the request form is in use; it is dismissed
        // explicitly (Cancel / Back / outside click) instead of on blur.
        if (requestModeRef.current) {
            return;
        }
        setShowAdNetworkSelection(false);
    }, []);

    const closeAll = useCallback(() => {
        setRequestModeSafe(false);
        setShowAdNetworkSelection(false);
    }, [setRequestModeSafe]);

    const enterRequestMode = useCallback(
        name => {
            setRequestName(name);
            setRequestModeSafe(true);
            setShowAdNetworkSelection(true);
        },
        [setRequestModeSafe]
    );

    const backToSearch = useCallback(() => {
        setRequestModeSafe(false);
        setShowAdNetworkSelection(true);
    }, [setRequestModeSafe]);

    const handleSubmit = useCallback(
        data => {
            onIntegrationRequested(data);
            closeAll();
        },
        [onIntegrationRequested, closeAll]
    );

    // While the form is open, blur is suppressed (so internal focus changes don't close
    // it), so dismiss the form when the user clicks outside the dropdown.
    useEffect(() => {
        if (!requestMode) {
            return undefined;
        }
        const handleOutsideMouseDown = event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                closeAll();
            }
        };
        document.addEventListener('mousedown', handleOutsideMouseDown);
        return () => document.removeEventListener('mousedown', handleOutsideMouseDown);
    }, [requestMode, closeAll]);

    const renderNoOptionsMessage = useCallback(
        ({ inputValue }) => (
            <RequestIntegrationMessage
                inputValue={inputValue}
                translationPageKey={translationPageKey}
                onRequestClick={enterRequestMode}
            />
        ),
        [translationPageKey, enterRequestMode]
    );
    const noOptionsMessage = enableIntegrationRequest ? renderNoOptionsMessage : undefined;

    const isOpen = showAdNetworkSelection || requestMode;

    return (
        <>
            <AnimationItem pose={isOpen ? 'enter' : 'exit'} style={{ zIndex: Z_INDEX.SHOW }}>
                <div
                    id="uanNameText"
                    ref={containerRef}
                    className={css.uanNameText}
                    onBlur={hideAdNetworkSelectionHandler}
                    style={{
                        // Only establish a positioning context while the request form is open
                        // (it anchors absolutely to this div). Left unset otherwise to preserve
                        // the original behavior on the Data Destinations / Extract pages.
                        position: requestMode ? 'relative' : undefined,
                        width: '300px',
                        marginRight: '-1px',
                        right: isOpen ? 0 : '',
                        zIndex: isOpen ? Z_INDEX.SHOW : Z_INDEX.HIDE,
                    }}
                >
                    <AdNetworkSelection
                        onChange={onAdNetworkSelectionChange}
                        onBlur={hideAdNetworkSelectionHandler}
                        options={adNetworkOptions}
                        controlledValue={selectedAdNetworkOption}
                        placeHolder={placeholderText}
                        onInputChange={onInputChange}
                        setFocus={showAdNetworkSelection && !requestMode}
                        noOptionsMessage={noOptionsMessage}
                        menuIsOpen={requestMode ? false : undefined}
                    />
                    {requestMode && (
                        <RequestIntegrationForm
                            initialName={requestName}
                            translationPageKey={translationPageKey}
                            onBack={backToSearch}
                            onCancel={closeAll}
                            onSubmit={handleSubmit}
                        />
                    )}
                </div>
            </AnimationItem>
            <Button
                onClick={showAdNetworkSelectionHandler}
                onBlur={hideAdNetworkSelectionHandler}
                type="primary"
                level="level1"
                text={buttonText}
                forceActive={isOpen}
                style={{ borderRadius: isOpen ? '0 2px 2px 0' : '2px', zIndex: 4 }}
                Icon={icon}
            />
        </>
    );
};

AddNewUan.propTypes = {
    adNetworkOptions: PropTypes.arrayOf(PropTypes.any),
    selectedAdNetworkOption: PropTypes.objectOf(PropTypes.any),
    buttonText: PropTypes.string,
    placeholderText: PropTypes.string,
    onInputChange: PropTypes.func,
    onAdNetworkSelectionChange: PropTypes.func.isRequired,
    icon: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
            component: PropTypes.func,
        }),
    ]),
    enableIntegrationRequest: PropTypes.bool,
    onIntegrationRequested: PropTypes.func,
    translationPageKey: PropTypes.string,
};

export default AddNewUan;
